import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Grid, IconButton, TextField, Typography } from '@mui/material';
import { endOfDay, startOfDay } from 'date-fns';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { SnapshotAlarmLogReturn } from '../../types';
import { AlarmLogForm } from '../alarmLog/AlarmLogPage';
import { useAlarmLog } from '../alarmLog/api/getAlarmLog';
import { ModuleType } from '../analyzerModules/types';
import AnalyzerSelectLocal from '../analyzers/AnalyzerSelectLocal';
import { useGetAnalyzersByModule } from '../analyzers/hooks/useGetAnalyzersByModule';
import { useSnapshots } from '../snapshots/api/getSnapshots';
import SnapshotSelectLocal from '../snapshots/SnapshotSelectLocal';
import AlarmHistoryTraceDisplay from './AlarmHistoryTraceDisplay';

const AlarmWallHistory = () => {
  const { control, watch } = useForm<AlarmLogForm>({
    defaultValues: {
      analyzerId: -1,
      snapshotId: -1,
      fromDate: new Date(),
      toDate: new Date(),
    },
  });
  const analyzersForModule = useGetAnalyzersByModule(ModuleType.BASE);
  const { data: snapshots } = useSnapshots({ analyzerId: watch('analyzerId') });
  const { data: alarmLog } = useAlarmLog({
    analyzerId: watch('analyzerId'),
    snapshotId: watch('snapshotId'),
    fromDate: startOfDay(watch('fromDate')).toISOString(),
    toDate: endOfDay(watch('toDate')).toISOString(),
    config: {
      refetchInterval: 60000,
    },
  });
  const processLogItems = (alarmLog: SnapshotAlarmLogReturn[] | undefined) => {
    if (!alarmLog) return;
    const newLogItemsToDisplay: {
      logItemId: number;
      analyzerId: number;
      snapshotId: number;
      alarmId: number;
      timestamp: string;
    }[] = [];
    for (const log of alarmLog) {
      const includedSnapshots: number[] = [];
      for (const logItem of log.snapshot_alarm_log_items) {
        if (!includedSnapshots.includes(logItem.snapshot_id)) {
          newLogItemsToDisplay.push({
            logItemId: logItem.id,
            analyzerId: logItem.analyzer_id,
            snapshotId: logItem.snapshot_id,
            alarmId: log.alarm_id,
            timestamp: log.time,
          });
          includedSnapshots.push(logItem.snapshot_id);
        }
      }
    }
    return newLogItemsToDisplay;
  };
  const logItemsToDisplay = useMemo(
    () => processLogItems(alarmLog),
    [alarmLog]
  );
  const [pageStart, setPageStart] = useState(0);

  return (
    <Grid container spacing={2}>
      <Grid container item spacing={2}>
        <Grid item style={{ width: '15rem' }}>
          <Controller
            name='fromDate'
            control={control}
            render={({ field: { onChange, value } }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label='From'
                  inputFormat='MM/dd/yyyy'
                  value={value}
                  onChange={onChange}
                  renderInput={(params: any) => <TextField {...params} />}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item style={{ width: '15rem' }}>
          <Controller
            name='toDate'
            control={control}
            render={({ field: { onChange, value } }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label='To'
                  inputFormat='MM/dd/yyyy'
                  value={value}
                  onChange={onChange}
                  renderInput={(params: any) => <TextField {...params} />}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item style={{ minWidth: '12rem' }}>
          <Controller
            name='analyzerId'
            control={control}
            rules={{
              required: false,
            }}
            render={({ field: { onChange, value } }) => (
              <AnalyzerSelectLocal
                analyzers={analyzersForModule}
                selectedAnalyzerId={value}
                handleAnalyzerChanged={onChange}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item style={{ minWidth: '12rem' }}>
          <Controller
            name='snapshotId'
            control={control}
            rules={{
              required: false,
            }}
            render={({ field: { onChange, value } }) => (
              <SnapshotSelectLocal
                snapshots={snapshots}
                selectedSnapshotId={value}
                handleSnapshotChanged={onChange}
                fullWidth
              />
            )}
          />
        </Grid>
        {logItemsToDisplay && logItemsToDisplay.length > 0 && (
          <Grid item container xs justifyContent='flex-end' alignItems='center'>
            <Typography variant='body2'>
              Page {Math.floor(pageStart / 16) + 1} of{' '}
              {Math.ceil((logItemsToDisplay?.length || 0) / 16)}
            </Typography>
            <IconButton
              disabled={pageStart === 0}
              onClick={() => setPageStart((current) => current - 16)}
              size='small'
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              disabled={
                pageStart / 16 >=
                Math.floor((logItemsToDisplay?.length || 0) / 16)
              }
              onClick={() => setPageStart((current) => current + 16)}
              size='small'
            >
              <ChevronRight />
            </IconButton>
          </Grid>
        )}
      </Grid>
      <Grid container item>
        {watch('fromDate') > watch('toDate') ? (
          <Grid item xs={12}>
            <h3 style={{ color: 'red' }}>To Date is before From Date</h3>
          </Grid>
        ) : (
          logItemsToDisplay
            ?.slice(pageStart, pageStart + 16)
            .map((log) => (
              <AlarmHistoryTraceDisplay
                key={log.logItemId}
                logItemId={log.logItemId}
                analyzerId={log.analyzerId}
                snapshotId={log.snapshotId}
                alarmId={log.alarmId}
                timestamp={log.timestamp}
              />
            ))
        )}
      </Grid>
    </Grid>
  );
};

export default AlarmWallHistory;
